import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'hi', name: 'Hindi', native: 'हिंदी' },
  { code: 'bn', name: 'Bengali', native: 'বাংলা' },
  { code: 'ta', name: 'Tamil', native: 'தமிழ்' },
  { code: 'te', name: 'Telugu', native: 'తెలుగు' },
  { code: 'mr', name: 'Marathi', native: 'मराठी' },
  { code: 'gu', name: 'Gujarati', native: 'ગુજરાતી' },
  { code: 'kn', name: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ml', name: 'Malayalam', native: 'മലയാളം' },
  { code: 'pa', name: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  { code: 'or', name: 'Odia', native: 'ଓଡ଼ିଆ' },
  { code: 'as', name: 'Assamese', native: 'অসমীয়া' },
  { code: 'ur', name: 'Urdu', native: 'اردو' },
  { code: 'ar', name: 'Arabic', native: 'العربية' },
  { code: 'es', name: 'Spanish', native: 'Español' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LanguageSwitcher = ({ compact = false }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const changeLanguage = async (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('reasondms_language', code);
    setOpen(false);
    try {
      const token = localStorage.getItem('reasondms_token');
      if (token) {
        await fetch(`${API_URL}/api/multilingual/preference`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ language: code })
        });
      }
    } catch (_) { /* silent */ }
  };

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        data-testid="language-switcher-btn"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors ${compact ? 'px-2 py-1.5' : 'px-3 py-2'}`}
      >
        <Globe className="h-3.5 w-3.5 text-slate-500" />
        <span className="text-xs font-medium">{compact ? current.code.toUpperCase() : current.native}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 max-h-72 overflow-y-auto bg-white rounded-lg border border-slate-200 shadow-lg z-50">
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              data-testid={`lang-option-${lang.code}`}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-50 transition-colors ${lang.code === i18n.language ? 'bg-cyan-50 text-cyan-700 font-medium' : 'text-slate-600'}`}
            >
              <span className="w-5 text-center font-mono text-slate-400">{lang.code}</span>
              <span className="flex-1 text-left">{lang.native}</span>
              <span className="text-slate-400">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
